import type { StyledCaption } from '@neo4j-nvl/base';
import { getPropertyTypeDisplayName } from '@nx/neo4j-sdk';
import { propertyToString } from '@nx/neo4j-sdk/src/data-transforms/record-to-string';
import type { CaptionOption } from '@nx/state';
import { Objects } from '@nx/stdlib';
import { calculateDefaultNodeColors } from '@nx/word-color';

import {
  type DataLookupTable,
  type GraphItemMetaData,
  type NeoNode,
  type NeoRel,
  type NodeData,
  type NvlGraph,
  type RelData,
} from './graph-viz-context';

export const DEFAULT_NODE_COLOR = '#D9D9D9';
export const DEFAULT_REL_COLOR = '#D9D9D9';

const sortAlphabetically = (a = '', b = '') => a.toLowerCase().localeCompare(b.toLowerCase());

function getMostCommonColor(colors: string[]): string | null {
  const [firstColor] = colors;

  if (firstColor === undefined) {
    return null;
  }

  let mostCommonColor = firstColor;
  const colorCounts: Record<string, number> = {};
  for (const color of colors) {
    colorCounts[color] = (colorCounts[color] ?? 0) + 1;
  }

  let maxCount = 0;

  for (const [color, colorCount] of Object.entries(colorCounts)) {
    if (colorCount > maxCount) {
      maxCount = colorCount;
      mostCommonColor = color;
    }
  }

  return mostCommonColor;
}

function transformToItemMetada(arg: Record<string, string[]>) {
  return Object.entries(arg).reduce<Record<string, GraphItemMetaData>>((acc, [label, colors]) => {
    acc[label] = {
      totalCount: colors.length,
      mostCommonColor: getMostCommonColor(colors) ?? DEFAULT_NODE_COLOR,
    };
    return acc;
  }, {});
}

export const captionPriorityOrder = [/^name$/i, /^title$/i, /^label$/i, /name$/i, /description$/i, /^.+/];

export function getDefaultCaptionKey(options: CaptionOption[]): CaptionOption | undefined {
  const captionKeys = options.filter((option) => option.type === 'property').map((option) => option.captionKey);

  for (const regex of captionPriorityOrder) {
    const matchingKey = captionKeys.find((key) => regex.test(key));
    if (matchingKey !== undefined) {
      return {
        type: 'property',
        captionKey: matchingKey,
      };
    }
  }

  const typeOption = options.find((option) => option.type === 'type');
  if (typeOption) {
    return typeOption;
  }

  return options.find((option) => option.type === 'id');
}

const getDefaultNodeCaption = (item: NeoNode): StyledCaption[] => {
  const options: CaptionOption[] = Object.keys(item.properties).map((property) => ({
    type: 'property',
    captionKey: property,
  }));

  options.push({ type: 'id' as const }, { type: 'type' as const });

  const defaultCaptionKey = getDefaultCaptionKey(options);

  if (defaultCaptionKey?.type === 'property') {
    const caption = item.properties[defaultCaptionKey.captionKey];

    if (caption !== undefined) {
      const stringifiedCaption = propertyToString(caption);
      // remove quotes from string
      if (typeof caption === 'string') {
        return [{ value: stringifiedCaption.slice(1, -1) }];
      }
      return [{ value: stringifiedCaption }];
    }
  }

  const [firstLabel] = item.labels;
  if (defaultCaptionKey?.type === 'type' && firstLabel !== undefined) {
    return [{ value: firstLabel }];
  }

  return [{ value: item.id }];
};

// Properties can be huge, so we split them out into a separate lookup table
// to prevent them from making the physics slow
// here we also apply default colors & captions to nodes and relationships
// and sample colors for the sidepanel
// in the future we'll apply styling here as well
export function mapToNvlGraph(rawNodes: NeoNode[], rawRels: NeoRel[]): NvlGraph {
  const labelColorPairs: Record<string, string[]> = {};
  const typeColorPairs: Record<string, string[]> = {};
  const nodeData: Record<string, NodeData> = {};
  const relData: Record<string, RelData> = {};

  const nodes = rawNodes.map((node) => {
    nodeData[node.id] = {
      id: node.id,
      labelsSorted: [...node.labels].sort(sortAlphabetically),
      properties: Objects.mapValues(node.properties, propertyToString),
      propertyTypes: Objects.mapValues(node.properties, getPropertyTypeDisplayName),
      color: node.color ?? DEFAULT_NODE_COLOR,
    };

    const [firstLabel] = node.labels;
    const newNode = {
      color: firstLabel !== undefined ? calculateDefaultNodeColors(firstLabel).backgroundColor : DEFAULT_NODE_COLOR,
      captions: getDefaultNodeCaption(node),
      ...node,
      properties: undefined,
      labels: undefined,
    };

    node.labels.forEach((label) => {
      labelColorPairs[label] = [...(labelColorPairs[label] ?? []), newNode.color];
    });

    return newNode;
  });

  const rels = rawRels.map((rel) => {
    relData[rel.id] = {
      id: rel.id,
      type: rel.type,
      properties: Objects.mapValues(rel.properties, propertyToString),
      propertyTypes: Objects.mapValues(rel.properties, getPropertyTypeDisplayName),
      color: rel.color ?? DEFAULT_NODE_COLOR,
    };

    typeColorPairs[rel.type] = [...(typeColorPairs[rel.type] ?? []), rel.color ?? DEFAULT_REL_COLOR];

    return {
      color: DEFAULT_REL_COLOR,
      captions: [{ value: rel.type }],
      ...rel,
      properties: undefined,
      type: undefined,
    };
  });

  const labelMetaData = transformToItemMetada(labelColorPairs);
  const typeMetaData = transformToItemMetada(typeColorPairs);

  const dataLookupTable: DataLookupTable = {
    nodes: nodeData,
    relationships: relData,
    labels: Object.keys(labelMetaData).sort((a, b) => sortAlphabetically(a, b)),
    types: Object.keys(typeMetaData).sort((a, b) => sortAlphabetically(a, b)),
    labelMetaData,
    typeMetaData,
  };

  return { nodes, rels, dataLookupTable };
}
